// src/components/Layout/AdminSidebar.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Nav,
  Badge,
  Button,
  Collapse,
  Offcanvas,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { NavLink, useLocation } from "react-router-dom";

const SIDEBAR_STORAGE_KEY = "adminSidebar.expanded";

const sections = [
  {
    heading: "Main",
    items: [
      { to: "/admin", label: "Dashboard", exact: true },
      { to: "/admin/schools", label: "Manage Schools" },
      { to: "/admin/users", label: "Manage Users" },
    ],
  },
  {
    heading: "System",
    items: [{ to: "/admin/settings", label: "Settings" }],
  },
];

function Item({ to, label, badge, disabled, exact = false, collapsed, onNavigate }) {
  const { pathname } = useLocation();

  const active = useMemo(() => {
    if (!to) return false;
    return exact ? pathname === to : pathname.startsWith(to);
  }, [pathname, to, exact]);

  const linkClasses = [
    "px-3 py-2 rounded-3 w-100 position-relative nav-link",
    active ? "fw-semibold" : "",
    disabled ? "disabled opacity-50" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <span className="d-inline-flex align-items-center justify-content-between w-100">
      <span className="d-inline-flex align-items-center gap-2">
        {/* little dot/glyph */}
        <span
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: 20,
            background: active ? "var(--bs-primary)" : "var(--bs-secondary-bg)",
            display: "inline-block",
            boxShadow: active ? "0 0 0 2px color-mix(in srgb, var(--bs-primary), transparent 85%)" : "none",
          }}
        />
        {!collapsed && <span className="text-truncate">{label}</span>}
      </span>
      {!collapsed && (badge ? <Badge bg="secondary">{badge}</Badge> : null)}
    </span>
  );

  const link = (
    <NavLink
      to={to}
      end={exact}
      onClick={onNavigate}
      className={({ isActive }) =>
        [
          linkClasses,
          // soft hover/active backgrounds using CSS vars so dark mode just works
          isActive || active ? "bg-body-secondary text-body" : "text-body",
        ].join(" ")
      }
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      aria-current={active ? "page" : undefined}
      style={{ transition: "background .16s ease, color .16s ease" }}
    >
      {/* active vertical rail */}
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: 6,
          top: 6,
          bottom: 6,
          width: 3,
          borderRadius: 4,
          background: active ? "var(--bs-primary)" : "transparent",
          transition: "background .16s ease",
        }}
      />
      {content}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Nav.Item as="li" className="mb-1">
        <OverlayTrigger placement="right" overlay={<Tooltip>{label}</Tooltip>}>
          <div>{link}</div>
        </OverlayTrigger>
      </Nav.Item>
    );
  }
  return (
    <Nav.Item as="li" className="mb-1">
      {link}
    </Nav.Item>
  );
}

function Section({ title, children, expanded }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-2">
      <Button
        variant="link"
        className="text-start w-100 px-3 py-2 text-muted"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={`section-${title}`}
      >
        <strong style={{ fontSize: 12, letterSpacing: 0.6 }}>
          {title.toUpperCase()}
        </strong>
      </Button>
      <Collapse in={open}>
        <div id={`section-${title}`} className="mt-1">
          <Nav as="ul" className="flex-column px-2">
            {React.Children.map(children, (child) =>
              React.cloneElement(child, { collapsed: !expanded })
            )}
          </Nav>
        </div>
      </Collapse>
    </div>
  );
}

function SidebarContent({
  expanded,
  toggleExpanded,
  brand,
  onLogout,
  onNavigate, // close drawer on mobile
  onToggleTheme, // optional theme toggle
  themeLabel = "Theme",
}) {
  return (
    <div className="d-flex flex-column h-100">
      {/* Header */}
      <div
        className="d-flex align-items-center justify-content-between px-3 py-3 border-bottom"
        style={{ minHeight: 64 }}
      >
        <div className="d-flex align-items-center gap-2 overflow-hidden" style={{ whiteSpace: "nowrap" }}>
          <div
            className="rounded-4 d-inline-flex align-items-center justify-content-center shadow-sm"
            style={{
              width: 40,
              height: 40,
              fontWeight: 700,
              fontSize: 16,
              border: "1px solid var(--bs-border-color)",
              background:
                "linear-gradient(135deg, color-mix(in srgb, var(--bs-primary), transparent 88%), var(--bs-body-bg))",
              color: "var(--bs-primary)",
            }}
            aria-hidden
          >
            {brand?.abbr || "A"}
          </div>
          {expanded && (
            <div className="d-flex flex-column">
              <span className="fw-bold text-truncate">{brand?.name || "Admin"}</span>
              {brand?.subtitle && <small className="text-muted">{brand.subtitle}</small>}
            </div>
          )}
        </div>

        <div className="d-flex align-items-center gap-2">
          {onToggleTheme && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onToggleTheme}
              title={themeLabel}
              aria-label={themeLabel}
            >
              <i className="bi bi-brightness-high" />
            </Button>
          )}
          <Button
            variant="light"
            size="sm"
            onClick={toggleExpanded}
            aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
            title={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? "«" : "»"}
          </Button>
        </div>
      </div>

      {/* Sections */}
      <nav className="flex-grow-1 py-2" aria-label="Primary">
        {sections.map((sec, idx) => (
          <Section key={idx} title={sec.heading} expanded={expanded}>
            {sec.items.map((it) => (
              <Item key={it.to || it.label} {...it} onNavigate={onNavigate} />
            ))}
          </Section>
        ))}
      </nav>

      {/* Footer */}
      <div className="mt-auto border-top px-2 py-2">
        {onLogout && (
          <Button
            variant="outline-secondary"
            className="w-100 d-flex align-items-center justify-content-center gap-2"
            onClick={onLogout}
          >
            <span aria-hidden>⎋</span>
            {expanded && <span>Logout</span>}
          </Button>
        )}
        <div className="text-center text-muted mt-2" style={{ fontSize: 12 }}>
          {expanded ? "v1.0.0" : "v1"}
        </div>
      </div>
    </div>
  );
}

export default function AdminSidebar({ brand, onLogout, onToggleTheme, themeLabel }) {
  const [expanded, setExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  // persist desktop expanded state
  useEffect(() => {
    const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (saved != null) setExpanded(saved === "1");
  }, []);
  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, expanded ? "1" : "0");
  }, [expanded]);

  const toggleExpanded = useCallback(() => setExpanded((v) => !v), []);
  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  return (
    <>
      {/* Mobile toggle bar */}
      <div className="d-lg-none position-sticky top-0 bg-body border-bottom px-2 py-2 z-3">
        <Button variant="primary" onClick={openMobile} aria-label="Open navigation">
          ☰ Menu
        </Button>
      </div>

      {/* Desktop sidebar */}
      <aside
        className="admin-sidebar d-none d-lg-flex flex-column bg-body"
        style={{
          width: expanded ? 268 : 76,
          transition: "width 180ms ease",
          borderRight: "1px solid var(--bs-border-color)",
          minHeight: "100vh",
          position: "sticky",
          top: 0,
        }}
        aria-label="Admin navigation"
      >
        <SidebarContent
          expanded={expanded}
          toggleExpanded={toggleExpanded}
          brand={brand}
          onLogout={onLogout}
          onToggleTheme={onToggleTheme}
          themeLabel={themeLabel}
        />
      </aside>

      {/* Mobile drawer */}
      <Offcanvas
        show={mobileOpen}
        onHide={closeMobile}
        placement="start"
        className="d-lg-none"
        aria-labelledby="offcanvas-nav-title"
        backdrop
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title id="offcanvas-nav-title">
            {brand?.name || "Menu"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <SidebarContent
            expanded
            toggleExpanded={() => {}}
            brand={brand}
            onLogout={onLogout}
            onNavigate={closeMobile}
            onToggleTheme={onToggleTheme}
            themeLabel={themeLabel}
          />
        </Offcanvas.Body>
      </Offcanvas>

      {/* polish */}
      <style>{`
        .admin-sidebar .nav-link:hover { text-decoration: none; background: var(--bs-secondary-bg); }
        .admin-sidebar .nav-link:focus-visible { outline: 2px solid var(--bs-primary); outline-offset: 2px; }
        .admin-sidebar .btn-link { text-decoration: none; }
      `}</style>
    </>
  );
}
